<template>
  <div class="bg-white shadow-sm border border-slate-200 overflow-hidden">
    <div class="px-6 pt-2 pb-1">
      <div class="flex items-baseline gap-2 flex-wrap">
        <h2 class="text-base font-bold text-[#0d4169] text-left">
          {{ resolvedCompanyName }}
        </h2>
        <span class="text-sm font-bold text-slate-600">Bank Register</span>
      </div>
    </div>

    <FinanceToast :show="toast.show" :variant="toast.variant">{{ toast.message }}</FinanceToast>

    <div v-if="isLoadingRows" class="px-6 py-12 flex justify-center items-center">
      <div class="h-8 w-8 border-2 border-[#0d4169]/30 border-t-transparent rounded-full animate-spin"></div>
    </div>

    <form v-else @submit.prevent="save" class="px-6 pt-2">
      <div class="grid grid-cols-1 md:grid-cols-4 items-end gap-3">
        <div class="flex flex-col gap-1">
          <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">Bank Name</label>
          <input
            v-model.trim="form.bankName"
            type="text"
            class="w-full px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm"
            placeholder="Bank Name"
            required
          >
        </div>

        <div class="flex flex-col gap-1">
          <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">Remark</label>
          <input
            v-model.trim="form.remark"
            type="text"
            class="w-full px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm"
            placeholder="Remark"
          >
        </div>

        <div class="flex items-end shrink-0 md:col-start-3 md:justify-self-start">
          <button
            v-if="editingId"
            type="submit"
            :disabled="!canEditBankRegister || isSaving"
            class="w-full md:w-auto min-w-max px-5 py-1.5 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all active:scale-95 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            <div
              v-if="isSaving"
              class="h-5 w-5 border-2 border-white/30 border-t-transparent rounded-full animate-spin"
            ></div>
            <span v-if="!isSaving">Update Changes</span>
          </button>
          <button
            v-else
            type="submit"
            :disabled="!canAddBankRegister || isSaving"
            class="w-full md:w-auto min-w-max px-5 py-1.5 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all active:scale-95 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            <div
              v-if="isSaving"
              class="h-5 w-5 border-2 border-white/30 border-t-transparent rounded-full animate-spin"
            ></div>
            <span v-if="!isSaving">Save</span>
          </button>
        </div>
      </div>
    </form>

    <div v-if="!isLoadingRows" class="px-6 pt-4 pb-6">
      <div class="w-full max-lg:overflow-x-auto">
        <div class="inline-block w-full max-w-2xl">
          <table class="w-full max-lg:min-w-[520px] border-collapse bg-white rounded-xl overflow-hidden shadow-sm">
            <caption class="caption-top text-left pb-2 text-[0.78rem] font-semibold text-[#1e293b]">
              Banks
            </caption>
            <thead>
              <tr>
                <th>No.</th>
                <th>Name</th>
                <th>Remark</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, idx) in entries"
                :key="row.id"
                class="hover:bg-slate-100/50 transition-colors border-b"
              >
                <td>{{ idx + 1 }}</td>
                <td>{{ row.bankName }}</td>
                <td class="remark-cell">{{ row.remark || '—' }}</td>
                <td class="actions">
                  <button
                    type="button"
                    :disabled="!canEditBankRegister"
                    @click="editEntry(row)"
                  >
                    Edit
                  </button>
                  <label
                    class="toggle-switch"
                    :class="{ 'toggle-switch--disabled': !canEditBankRegister }"
                    title="Active"
                  >
                    <input
                      type="checkbox"
                      :checked="row.active"
                      :disabled="!canEditBankRegister"
                      @change="toggleActive(row)"
                    >
                    <span class="slider"></span>
                  </label>
                </td>
              </tr>
              <tr v-if="entries.length === 0">
                <td colspan="4" style="text-align:center; color:#94a3b8; padding: 32px 15px; font-style: italic;">
                  No banks saved.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import FinanceToast from '../FinanceToast.vue'
import { useStore } from 'vuex'
import { apiFetch } from '../../../utils/api'

const store = useStore()

const currentUser = computed(() => store.getters.currentUser || store.state.user || {})

const userPrivileges = computed(() => {
  const raw = currentUser.value?.privilege
  if (!raw) return {}
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw)
    } catch {
      return {}
    }
  }
  return raw
})

const hasAllPrivilege = computed(() => {
  return userPrivileges.value?.All === true || userPrivileges.value?.all === true
})

function getPrivilegeEntry(key) {
  const priv = userPrivileges.value || {}
  if (!key) return null
  if (priv?.[key]) return priv[key]

  const normalizedNeedle = String(key).trim().toLowerCase()
  const foundKey = Object.keys(priv).find((k) => String(k).trim().toLowerCase() === normalizedNeedle)
  return foundKey ? priv[foundKey] : null
}

function getActionFlag(entry, action) {
  if (!entry || typeof entry !== 'object') return false
  if (!action) return false
  if (entry[action] !== undefined) return !!entry[action]

  const a = String(action)
  const candidates = [
    a.toLowerCase(),
    a.toUpperCase(),
    a.charAt(0).toUpperCase() + a.slice(1).toLowerCase()
  ]
  for (const k of candidates) {
    if (entry[k] !== undefined) return !!entry[k]
  }
  return false
}

const canAddBankRegister = computed(() => {
  if (hasAllPrivilege.value) return true
  return getActionFlag(getPrivilegeEntry('Bank Register'), 'Add')
})

const canEditBankRegister = computed(() => {
  if (hasAllPrivilege.value) return true
  return getActionFlag(getPrivilegeEntry('Bank Register'), 'Edit')
})

const resolvedCompanyName = computed(() => {
  const user = currentUser.value
  return (
    user?.company?.name ||
    user?.companyName ||
    user?.company_name ||
    user?.company?.company_name ||
    'Company Name'
  )
})

const form = ref({
  bankName: '',
  remark: ''
})

const editingId = ref(null)
const entries = ref([])
const isLoadingRows = ref(false)
const isSaving = ref(false)
const toast = ref({ show: false, message: '', variant: 'success' })
let toastTimer = null

function showToast(message, variant = 'success') {
  if (toastTimer) {
    window.clearTimeout(toastTimer)
    toastTimer = null
  }
  toast.value = { show: true, message, variant }
  const duration = variant === 'error' ? 5000 : 3000
  toastTimer = window.setTimeout(() => {
    toast.value = { show: false, message: '', variant: 'success' }
    toastTimer = null
  }, duration)
}

function resetForm() {
  form.value = { bankName: '', remark: '' }
  editingId.value = null
}

async function loadEntries() {
  isLoadingRows.value = true
  try {
    const res = await apiFetch('/finance/bank-register')
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      showToast(err.message || 'Failed to load banks.', 'error')
      entries.value = []
      return
    }
    const data = await res.json().catch(() => [])
    entries.value = Array.isArray(data) ? data : []
  } catch {
    showToast('Failed to load banks.', 'error')
    entries.value = []
  } finally {
    isLoadingRows.value = false
  }
}

async function save() {
  if (isSaving.value) return
  if (editingId.value != null) {
    if (!canEditBankRegister.value) return
  } else if (!canAddBankRegister.value) {
    return
  }

  const bankName = String(form.value.bankName || '').trim()
  const remark = String(form.value.remark ?? '').trim()
  if (!bankName) return

  const body = { bankName, remark }

  isSaving.value = true
  try {
    if (editingId.value != null) {
      const res = await apiFetch(`/finance/bank-register/${editingId.value}`, {
        method: 'PUT',
        body: JSON.stringify(body)
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        showToast(err.message || 'Failed to update bank.', 'error')
        return
      }
      showToast('Bank updated successfully.', 'success')
      resetForm()
      await loadEntries()
      return
    }

    const res = await apiFetch('/finance/bank-register', {
      method: 'POST',
      body: JSON.stringify(body)
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      showToast(err.message || 'Failed to save bank.', 'error')
      return
    }
    showToast('Bank saved successfully.', 'success')
    resetForm()
    await loadEntries()
  } catch {
    showToast('Failed to save bank.', 'error')
  } finally {
    isSaving.value = false
  }
}

function editEntry(row) {
  if (!canEditBankRegister.value) return
  editingId.value = row.id
  form.value = {
    bankName: row.bankName || '',
    remark: row.remark != null ? String(row.remark) : ''
  }
}

async function toggleActive(row) {
  if (!canEditBankRegister.value) return
  const nextActive = !row.active
  try {
    const res = await apiFetch(`/finance/bank-register/${row.id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ active: nextActive })
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      showToast(err.message || 'Failed to update status.', 'error')
      return
    }
    const idx = entries.value.findIndex((r) => r.id === row.id)
    if (idx >= 0) entries.value[idx] = { ...entries.value[idx], active: nextActive }
  } catch {
    showToast('Failed to update status.', 'error')
  }
}

onMounted(() => {
  loadEntries()
})
</script>

<style scoped>
table {
  font-family: 'Segoe UI', sans-serif;
  font-size: 0.8rem;
  color: #1e293b;
}

th,
td {
  padding: 6px 8px;
  text-align: left;
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
  font-size: 0.7rem;
}

th {
  background: #f1f5f9;
  font-weight: 600;
}

.remark-cell {
  white-space: normal;
  max-width: 14rem;
  word-break: break-word;
  color: #334155;
}

tbody tr:hover {
  background: #f1f5f9;
}

.actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.actions button {
  background: none;
  border: none;
  color: #2563eb;
  cursor: pointer;
  text-decoration: underline;
  font-size: 0.85rem;
  padding: 0;
  display: flex;
  align-items: center;
}

.actions button:disabled {
  color: #94a3b8;
  cursor: not-allowed;
  text-decoration: none;
}

.toggle-switch {
  position: relative;
  display: inline-block;
  width: 28px;
  height: 15px;
  cursor: pointer;
}

.toggle-switch--disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  inset: 0;
  background: #cbd5e1;
  border-radius: 34px;
  transition: 0.2s;
}

.slider::before {
  content: '';
  position: absolute;
  width: 10px;
  height: 10px;
  left: 2px;
  top: 2.5px;
  background: white;
  border-radius: 50%;
  transition: 0.2s;
}

.toggle-switch input:checked + .slider {
  background: #22c55e;
}

.toggle-switch input:not(:checked) + .slider {
  background: #ef4444;
}

.toggle-switch input:checked + .slider::before {
  transform: translateX(13px);
}
</style>
